import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import {
  Loader2, Calendar, MapPin, Search, ExternalLink, ArrowLeft, Sparkles,
} from "lucide-react";

/**
 * Daily Pulse — public evergreen "3 things happening in {city} on {date}"
 * pages. Two routes render from this component:
 *   /daily                      → hero + search
 *   /daily/:city                → most-recent snapshot for that city
 *   /daily/:city/:date          → one specific day
 * All three are indexable, shareable, always available.
 */
export default function DailyPulse() {
  const params = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();

  const city = params.city || search.get("city") || "";
  const date = params.date || "";
  const q = search.get("q") || "";

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);

  // Load the archive page(s) whenever the URL changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load() {
      try {
        if (q) {
          const r = await http.get(`/daily/search`, { params: { q, city, limit: 40 } });
          if (!cancelled) setResults(r.data);
          if (!cancelled) setDoc(null);
        } else if (city && date) {
          const r = await http.get(`/daily/${encodeURIComponent(city)}/${date}`);
          if (!cancelled) { setDoc(r.data); setResults(null); }
        } else if (city) {
          const r = await http.get(`/daily/latest/${encodeURIComponent(city)}`);
          if (!cancelled) { setDoc(r.data); setResults(null); }
        } else {
          if (!cancelled) { setDoc(null); setResults(null); }
        }
      } catch {
        if (!cancelled) { setDoc(null); setResults(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [city, date, q]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const form = e.target;
    const q2 = new FormData(form).get("q")?.toString().trim() || "";
    const city2 = new FormData(form).get("city")?.toString().trim() || "";
    if (q2) {
      const p = new URLSearchParams();
      p.set("q", q2);
      if (city2) p.set("city", city2);
      navigate({ pathname: "/daily", search: `?${p.toString()}` });
    } else if (city2) {
      navigate(`/daily/${encodeURIComponent(city2)}`);
    }
  };

  return (
    <article className="max-w-3xl mx-auto px-5 py-10" data-testid="daily-pulse-page">
      {/* Header */}
      <div className="mb-8">
        <Link to="/" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100">
          <ArrowLeft className="w-4 h-4" /> Social Pulse
        </Link>
        <div className="mt-4 flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: "#7C3AED" }}>
          <Sparkles className="w-3.5 h-3.5" /> DAILY PULSE ARCHIVE
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-2" data-testid="daily-title" style={{ color: "#1E1B4B" }}>
          {doc
            ? doc.title
            : q
              ? `Searching "${q}"`
              : city
                ? `Daily Pulse — ${city}`
                : "3 things happening near you, every day"}
        </h1>
        {doc && (
          <div className="mt-2 text-sm opacity-70 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {doc.city}{doc.country ? `, ${doc.country}` : ""}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {doc.date}</span>
            {doc.slug && (
              <Link to={`/l/${doc.slug}`} className="inline-flex items-center gap-1 text-sm underline" style={{ color: "#2563EB" }}>
                Full profile <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Search bar */}
      <form onSubmit={submitSearch} className="rounded-2xl border p-3 flex flex-wrap gap-2 mb-8"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card,#fff)" }}>
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 opacity-60" />
          <input
            name="q"
            defaultValue={q}
            placeholder="Search past days (festivals, causes, headlines…)"
            className="w-full outline-none bg-transparent text-sm"
            data-testid="daily-search-q"
          />
        </div>
        <input
          name="city"
          defaultValue={city || ""}
          placeholder="City"
          className="outline-none bg-transparent text-sm border-l px-2"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="daily-search-city"
        />
        <button type="submit" className="wofy-btn-primary text-sm" data-testid="daily-search-submit">Search</button>
      </form>

      {loading && (
        <div className="py-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" /></div>
      )}

      {/* Single-day view */}
      {!loading && doc && (
        <div className="space-y-4" data-testid="daily-items">
          {(doc.items || []).map((it, i) => (
            <a
              key={i}
              href={it.url || "#"}
              target={it.url?.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="block p-4 rounded-xl border hover:shadow-md transition"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card,#fff)" }}
              data-testid={`daily-item-${i}`}
            >
              <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">{it.badge}</div>
              <div className="font-semibold text-lg leading-tight flex items-start gap-1">
                <span>{it.title}</span>
                {it.url?.startsWith("http") && <ExternalLink className="w-3.5 h-3.5 mt-1 opacity-60" />}
              </div>
              {it.blurb && <p className="text-sm opacity-80 mt-1">{it.blurb}</p>}
            </a>
          ))}
        </div>
      )}

      {/* Search results */}
      {!loading && results && (
        <div className="space-y-3" data-testid="daily-search-results">
          <div className="text-xs opacity-60">{results.count} archived days</div>
          {(results.items || []).map((d, i) => (
            <Link
              key={i}
              to={`/daily/${encodeURIComponent(d.city)}/${d.date}`}
              className="block p-4 rounded-xl border hover:shadow-md transition"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card,#fff)" }}
            >
              <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">
                {d.date} · {d.city}
              </div>
              <div className="font-semibold text-base">{d.title}</div>
              {(d.items || []).slice(0, 2).map((it, ii) => (
                <div key={ii} className="text-sm opacity-80 line-clamp-1 mt-1">
                  <span className="opacity-60">·</span> {it.title}
                </div>
              ))}
            </Link>
          ))}
          {results.count === 0 && (
            <div className="py-14 text-center opacity-60 text-sm">
              No archived days matched. Try a different query or city.
            </div>
          )}
        </div>
      )}

      {/* Empty landing view */}
      {!loading && !doc && !results && !q && !city && (
        <div className="py-16 text-center opacity-70" data-testid="daily-empty">
          <p className="text-sm">Enter a city in the box above to see today's Daily Pulse — or search past days.</p>
        </div>
      )}

      {/* SEO breadcrumb + share hint */}
      {doc && (
        <div className="mt-10 pt-6 border-t text-xs opacity-60" style={{ borderColor: "var(--wofy-border)" }}>
          Permanent link: <span className="font-mono">/daily/{doc.city}/{doc.date}</span> · Share this page anywhere.
        </div>
      )}
    </article>
  );
}
